import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Soft-Launch Readiness Widget — Autonomous COO layer.
 * Surfaces the 7 gates from services/coo_autonomous.compute_soft_launch_readiness
 * and lets Shannon trigger a manual COO cycle + peek at the autonomous decisions log.
 */
const SoftLaunchReadiness = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/coo/soft-launch`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setErr("");
    } catch (e) {
      setErr(e.message || "Could not load soft-launch readiness.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDecisions = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/coo/decisions?limit=15`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setDecisions(d.rows || []);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/coo/run-now`, {
        method: "POST", credentials: "include",
      });
      if (r.ok) {
        await load();
        await loadDecisions();
        setDecisionsOpen(true);
      } else {
        const d = await r.json().catch(() => ({}));
        alert(`Could not run COO cycle: ${d.detail || r.status}`);
      }
    } catch {
      alert("Network error running COO cycle.");
    } finally {
      setRunning(false);
    }
  };

  const toggleDecisions = () => {
    const next = !decisionsOpen;
    setDecisionsOpen(next);
    if (next && decisions.length === 0) loadDecisions();
  };

  if (loading) return null;
  if (err) return null; // fail-quiet on the Cockpit — this is a supplementary widget

  const passed = data?.passed || 0;
  const total = data?.total || 7;
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const ready = !!data?.ready_for_soft_launch;
  const stalled = passed < 4;

  const bandColor = ready ? "#00DC78" : stalled ? "#F87171" : "#FFB347";
  const bandBg    = ready ? "rgba(0,220,120,0.10)"
                          : stalled ? "rgba(220,38,38,0.14)"
                                    : "rgba(255,179,71,0.12)";
  const bandBorder = ready ? "rgba(0,220,120,0.45)"
                           : stalled ? "rgba(220,38,38,0.5)"
                                     : "rgba(255,179,71,0.45)";

  const statusHeadline = ready
    ? "🟢 Go for soft-launch. All critical gates cleared."
    : stalled
      ? "🔴 Foundational gates still open — see the checklist below."
      : "🟡 Close — a few gates left before you can accept your first paid contract.";

  return (
    <div data-testid="soft-launch-readiness" style={{
      background: bandBg,
      border: `1px solid ${bandBorder}`,
      borderRadius: 8,
      padding: "0.9rem 1.1rem",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.7rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
          <span data-testid="soft-launch-dot" style={{
            width: 12, height: 12, borderRadius: "50%",
            background: bandColor,
            boxShadow: `0 0 10px ${bandColor}`,
            flexShrink: 0,
          }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#B8915A", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
              Digital Platform · Soft-Launch Readiness
            </div>
            <div data-testid="soft-launch-headline" style={{ color: "#F3F4F6", fontSize: "0.92rem", fontWeight: 700, marginTop: 3 }}>
              {statusHeadline}
            </div>
            <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 2 }}>
              <strong data-testid="soft-launch-score" style={{ color: bandColor }}>{passed}/{total}</strong> gates green · {pct}% complete
              {data?.computed_at && <> · last checked {new Date(data.computed_at).toLocaleTimeString()}</>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="soft-launch-run-now"
            onClick={runNow}
            disabled={running}
            style={{
              background: running ? "rgba(184,145,90,0.2)" : "linear-gradient(90deg, #B8915A, #A17747)",
              color: running ? "#B8915A" : "#1b2a4e",
              border: "1px solid #B8915A",
              padding: "0.35rem 0.85rem",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 800,
              cursor: running ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {running ? "Running…" : "🤖 Run COO Now"}
          </button>
          <button
            type="button"
            data-testid="soft-launch-decisions-toggle"
            onClick={toggleDecisions}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#ADBED1",
              padding: "0.35rem 0.85rem",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {decisionsOpen ? "Hide decisions" : "📜 Decisions log"}
          </button>
          <button
            type="button"
            data-testid="soft-launch-expand-toggle"
            onClick={() => setExpanded(!expanded)}
            style={{
              background: "transparent",
              border: `1px solid ${bandColor}`,
              color: bandColor,
              padding: "0.35rem 0.85rem",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {expanded ? "Hide gates" : `Show ${total} gates`}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginTop: "0.7rem", background: "rgba(0,0,0,0.28)", borderRadius: 3, height: 6, overflow: "hidden" }}>
        <div data-testid="soft-launch-progress-bar" style={{
          width: `${pct}%`,
          height: "100%",
          background: bandColor,
          transition: "width 400ms ease",
        }} />
      </div>

      {/* Gate list */}
      {expanded && (
        <div data-testid="soft-launch-gates" style={{ marginTop: "0.8rem", display: "grid", gap: "0.35rem" }}>
          {(data?.gates || []).map((g) => {
            const gDot = g.passed ? "#00DC78" : "#F87171";
            return (
              <div key={g.key} data-testid={`soft-launch-gate-${g.key}`}
                   style={{ display: "flex", alignItems: "center", gap: "0.6rem",
                            background: "rgba(0,0,0,0.18)", padding: "0.45rem 0.75rem",
                            borderRadius: 4, borderLeft: `3px solid ${gDot}` }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: gDot, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#F3F4F6", fontWeight: 600, fontSize: "0.82rem" }}>{g.label}</div>
                </div>
                <div style={{ color: g.passed ? "#00DC78" : "#FFB347", fontSize: "0.76rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {g.value}/{g.target} {g.passed ? "✓" : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Decisions log */}
      {decisionsOpen && (
        <div data-testid="soft-launch-decisions" style={{ marginTop: "0.8rem", display: "grid", gap: "0.35rem" }}>
          <div style={{ color: "#B8915A", fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 800, marginBottom: 2 }}>
            Digital Platform Decisions · Newest first
          </div>
          {decisions.length === 0 && (
            <div style={{ color: "#9CA3AF", fontSize: "0.8rem", padding: "0.5rem 0.2rem" }}>
              No platform actions yet. Tap <em>Run COO Now</em> to trigger a cycle.
            </div>
          )}
          {decisions.map((d) => (
            <div key={d.decision_id} data-testid={`decision-${d.decision_id}`}
                 style={{ background: "rgba(0,0,0,0.2)", padding: "0.5rem 0.75rem", borderRadius: 4,
                          borderLeft: "3px solid #5AC8D8" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                <div style={{ color: "#F3F4F6", fontSize: "0.82rem", fontWeight: 700 }}>
                  {d.kind} → {d.decision}
                </div>
                <div style={{ color: "#9CA3AF", fontSize: "0.72rem" }}>
                  {d.created_at ? new Date(d.created_at).toLocaleString() : ""}
                </div>
              </div>
              {d.rationale && (
                <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 3 }}>{d.rationale}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SoftLaunchReadiness;
